import _ from 'lodash';
import moment from 'moment-timezone';
import Highcharts from 'highcharts';

import {
  getLegendName,
  getSeriesName,
  checkNameForFormulaLegendName,
} from '../utils/chart/ChartManagementFeatures/getChartFeatures';
import {
  chartWithGroups,
  grandTotal,
} from '../utils/chart/ChartManagementFeatures/showChartFeatures';

import { average, median, stringifyNull } from '../utils/common';
import ColumnChartTooltip from '../components/tooltip/ColumnChartTooltip';
import { chartAxes } from '../constants/event';
import {
  sortingTypes,
  sortingOrder,
  sortingOrderBtnText,
  sortingTypeBtnText,
} from '../constants/common';
import { groupByFields } from '../utils/collection';
import { types } from '../constants/filter';
import { revenueQueryTypes } from '../constants/revenue';
import updateYaxisPlotLines from './ChartPlotline';
import {
  doTitle,
  toggleMenuItemTitle,
  toggleShowAllButton,
  toMenuItemTitle,
  getSortedSeries,
  updateUserOptionsEventSortOrder,
  toggleButtonText,
  updateSeriesWithSortedLegend,
  updateUserOptionsEventSortType,
  updateYAxis,
} from './Common';

const getRoundedPercentString = (percentage) => `${Math.round(percentage * 100) / 100}%`;

function withGrandTotal(series) {
  const seriesData = series.data;
  const data = [];
  let total = 0;
  for (let i = 0; i < seriesData.length; i++) {
    total += seriesData[i][1];
    data.push([stringifyNull(seriesData[i][0]), seriesData[i][1]]);
  }
  data.push([grandTotal, total]);
  return { ...series, data };
}

const fillData = (category, seriesData) => {
  const foundVal = seriesData.find((s) => String(s[0]) === String(category));

  return foundVal || [category, 0];
};

const stringifyNullCategories = (arr) => (arr[0] !== null ? arr : ['null', arr[1]]);

const group = (data, options) => {
  return {
    name: data.map(({ id, name }) => `${id}.${name}`).join(' '),
    series: data
      .flatMap(({ series, name, id, isSplitBy }) =>
        series.map((s) => {
          const sName = checkNameForFormulaLegendName(
            getSeriesName(s, name, id, isSplitBy),
            s.parentId,
            isSplitBy,
            options.legendName
          );
          return {
            ...s,
            name: sName,
            parentName: `${id}.${name}`,
          };
        })
      )
      .map((series, index) => ({ ...series, id: index })),
    categories: [...new Set(data.flatMap((chartData) => chartData.categories))],
  };
};

const toSeriesMapper = (categories) => (series) => {
  const data = categories
    .map((category) => fillData(category, series.data))
    .map(stringifyNullCategories)
    .sort((a, b) => a[1] - b[1]);

  const total = data.reduce((sum, [, y]) => sum + y, 0);
  const { parentId, color, legendSymbol, legendIndex, dataSum } = series;

  return {
    ...series,
    type: 'column',
    stack: parentId,
    data,
    total,
    color,
    marker: {
      symbol: legendSymbol,
    },
    legendIndex,
    dataSum,
  };
};

const getValues = (series, categories) =>
  categories.flatMap((category) =>
    series.map((s) => ({
      ...s,
      stack: s.stack,
      id: s.id,
      category,
      value: Object.fromEntries(s.data)[category] || 0,
    }))
  );

const orderMult = (order) => (order === sortingOrder.DESCENDING ? -1 : 1);

export const sortColumnsByX = (order, typeGroupBy) => {
  const compareFn = ([a, b]) => {
    switch (typeGroupBy) {
      case types.NUMBER:
        if (Number.isNaN(Number(a))) {
          return -orderMult(order);
        }
        if (Number.isNaN(Number(b))) {
          return orderMult(order);
        }
        return (Number(a) - Number(b)) * orderMult(order);
      case types.DATE:
      case types.COHORT:
        return (new Date(a) - new Date(b)) * orderMult(order);
      default:
        return a.localeCompare(b) * orderMult(order);
    }
  };

  return (a, b) => compareFn(Array.isArray(a) ? [a[0], b[0]] : [a, b]);
};

const sortColumnsByY = (order) => {
  return (a, b) => (a[1] - b[1]) * orderMult(order);
};

const getSortFn = ({ order, axis }, typeGroupBy) =>
  axis === chartAxes.Y ? sortColumnsByY(order, typeGroupBy) : sortColumnsByX(order, typeGroupBy);

// колонки в рамках категории рассматривается как одно целое
const getSortingColumns = (flatValues) =>
  Object.entries(
    flatValues.reduce(
      (res, item) => {
        res[item.category] += item.value;
        return res;
      },
      flatValues.reduce((res, { category }) => {
        res[category] = 0;
        return res;
      }, {})
    )
  );

// возвращает массив значений, которые соответствуют каждой колонке в графике
const getAllColumns = (filteredSeries, groupedValues) => {
  const filteredIds = filteredSeries.map(({ options }) => options.id);
  const filteredStacks = filteredSeries.map(({ options }) => options.stack);
  return groupedValues
    .filter(({ key }) => filteredStacks.includes(key.stack) && key.category !== grandTotal)
    .map(({ data }) =>
      data
        .filter(({ id }) => filteredIds.includes(id))
        .reduce((res, { value }) => {
          let resVar = res;
          resVar += value;

          return resVar;
        }, 0)
    );
};

// данные в серии приводятся в порядок, который соответствует данным в отсортированных категориях
const sortSeries = (sortedCategories) => (series) => {
  const objData = Object.fromEntries(series.data);
  return {
    ...series,
    data: sortedCategories
      .map((category) => [category, objData[category]])
      .filter(([, value]) => value),
  };
};

export const toColumnConfig = (
  data,
  options = {},
  showGrandTotal,
  fromDashboard = false,
  revenueType,
  showSort
) => {
  const { series, categories = [] } = group(data, options);
  const toOriginalSeries = toSeriesMapper(categories);
  let originalSeries = series.map(toOriginalSeries);
  // содержат массив объектов состоящий из: значения, стека, категории
  const flatValues = getValues(originalSeries, categories);
  // сгруппированные значения по 2 полям(используется для average и median)
  const groupedValues = groupByFields(flatValues, 'stack', 'category');

  // сортировка
  const columns = getSortingColumns(flatValues);
  const sortedCategories = columns
    .sort(getSortFn(options.columnChartSorting, data[0].typeGroupBy))
    .map(([category]) => category);

  // сортировка серий(недостаточно сортировать только категории, так как потом будут проблемы при отрисовке графиков)
  originalSeries = originalSeries.map(sortSeries(sortedCategories));

  const seriesSortOrder = sortingOrder.ASCENDING;
  const seriesSortType = sortingTypes.NAME;

  originalSeries = getSortedSeries(originalSeries, seriesSortOrder, seriesSortType);

  const columnChartConfig = {
    grandTotal: !!options.calcTotal,
    medianLine: !!options.medianLine,
    averageLine: !!options.averageLine,
    typeSorting: data[0].typeGroupBy,
    time: {
      getTimezoneOffset(timestamp) {
        return -moment.tz(timestamp, options.timezone).utcOffset();
      },
    },
    groups: ['median', 'average', 'grandTotal', 'sort', options.group],
    eventSort: {
      sortOrder: seriesSortOrder,
      sortType: seriesSortType,
    },
    chart: {
      zooming: {
        type: 'x',
      },
      events: {
        redraw({ target: chart }) {
          updateYAxis(chart);
          if (!!chart.legend && chart.legend.title !== undefined) {
            chart.legend.title.attr('x', chart.legend.legendWidth / 2);
            chart.legend.title.attr('text-anchor', 'middle');
          }
          if (chart.series[0]) {
            if (chart.showAll !== chart.series.every((s) => s.visible)) {
              toggleShowAllButton(chart);
            }

            const values = chart.series.flatMap((s) => s.data.map((v) => v.y));
            const plotLines = [];
            if (values.some((v) => v < 0)) {
              plotLines.push({
                id: 'zero',
                lineColor: 'black',
                dashStyle: 'solid',
                text: '',
                textColor: '',
                value: 0,
              });
            }

            if (!chart.userOptions.averageLine && !chart.userOptions.medianLine) {
              updateYaxisPlotLines(plotLines, chart);
              return;
            }

            const filteredSeries = chart.series.filter((s) => s.visible);
            const allColumns = getAllColumns(filteredSeries, groupedValues);

            if (chart.userOptions.averageLine) {
              const averageValue = _.round(average(allColumns), 2);
              plotLines.push({
                id: 'averagePlotLine',
                lineColor: 'orange',
                dashStyle: 'dash',
                text: `Avg = ${averageValue}`,
                textColor: 'orange',
                value: averageValue,
              });
            }
            if (chart.userOptions.medianLine) {
              const medianValue = _.round(median(allColumns), 2);
              plotLines.push({
                id: 'medianPlotLine',
                lineColor: 'red',
                dashStyle: 'solid',
                text: `Median = ${medianValue}`,
                textColor: 'red',
                value: medianValue,
              });
            }
            updateYaxisPlotLines(plotLines, chart);
          }
        },
      },
    },
    tooltip: {
      useHTML: true,
      formatter() {
        const isGrandTotal = this.series.data.some((s) => s.name === grandTotal);
        const isSplit = series.length > 1;
        const isPaying = revenueType === revenueQueryTypes.PAYERS;
        return ColumnChartTooltip(isSplit, isGrandTotal, this, isPaying);
      },
      zIndex: 10,
    },
    title: {
      text: '',
    },
    yAxis: {
      maxPadding: 0.05,
      title: {
        text: null,
      },
      stackLabels: {
        crop: false,
        overflow: 'allow',
        enabled: true,
        formatter() {
          const sumColumns = this.axis.series
            .filter((s) => s.visible)
            .map((s) => originalSeries.find((o) => o.name === s.name))
            .filter((s) => !!s)
            .map((s) => s.total)
            .reduce((a, b) => a + b, 0);
          let percentage = (100 * this.total) / sumColumns;
          // eslint-disable-next-line no-restricted-globals
          percentage = isNaN(percentage) ? 0 : percentage;
          return `${_.round(this.total, 2)} (${getRoundedPercentString(percentage)})`;
        },
        style: {
          color: 'gray',
          textOutline: '1px white',
        },
      },
    },
    xAxis: {
      type: 'category',
      originalCategories: [...sortedCategories],
      categories: options.calcTotal ? [...sortedCategories, 'Grand total'] : [...sortedCategories],
      min: null,
      max: null,
      events: {
        afterSetExtremes({ min, max }) {
          Highcharts.charts
            .filter((chart) => chartWithGroups(chart, 'zoomSync', options.group))
            .forEach((chart) => chart.xAxis[0].setExtremes(min, max));
        },
      },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        borderWidth: 0,
        groupPadding: 0.1,
      },
      series: {
        events: {
          legendItemClick() {
            // принудительная перерисовка графика, так как при скрытии графика по клику на легенду остаются stackLabels
            this.redraw();
          },
        },
      },
    },
    originalSeries,
    series: options.calcTotal ? originalSeries.map(withGrandTotal) : originalSeries,
    legend: {
      title: {
        text: getLegendName(data, options?.legendName),
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
      maxHeight: 117,
    },
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          menuItems: [
            'grandTotal',
            'averageLine',
            'medianLine',
            'separator',
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'downloadPDF',
            'downloadSVG',
          ],
        },
        Fullscreen: {
          verticalAlign: 'bottom',
          align: 'left',
          text: 'full screen',
          onclick() {
            this.fullscreen.toggle();
          },
        },
        showAll: {
          verticalAlign: 'bottom',
          align: 'left',
          text: doTitle('show all', false),
          onclick() {
            if (this.series.reduce((r, s) => s.visible && r, true)) {
              this.series.forEach((s) => {
                s.setVisible(false, false);
              });
            } else {
              this.series.forEach((s) => {
                s.setVisible(true, false);
              });
            }

            toggleShowAllButton(this);
            this.redraw();
          },
        },
        ...(showSort && {
          toggleSortOrder: {
            verticalAlign: 'bottom',
            align: 'left',
            text:
              seriesSortOrder === sortingOrder.ASCENDING
                ? sortingOrderBtnText.ASCENDING
                : sortingOrderBtnText.DESCENDING,
            onclick() {
              this.userOptions.eventSort = updateUserOptionsEventSortOrder(this);

              updateSeriesWithSortedLegend(
                this.series,
                options.calcTotal ? originalSeries.map(withGrandTotal) : originalSeries,
                this.userOptions.eventSort
              );

              toggleButtonText(
                this.exportSVGElements,
                this.userOptions.eventSort.sortOrder,
                sortingOrder.ASCENDING,
                sortingOrderBtnText.ASCENDING,
                sortingOrderBtnText.DESCENDING
              );

              this.redraw();
            },
          },
        }),
        ...(showSort && {
          toggleSortType: {
            verticalAlign: 'bottom',
            align: 'left',
            text:
              seriesSortType === sortingTypes.NAME
                ? sortingTypeBtnText.NAME
                : sortingTypeBtnText.SUM,
            onclick() {
              this.userOptions.eventSort = updateUserOptionsEventSortType(this);

              updateSeriesWithSortedLegend(
                this.series,
                options.calcTotal ? originalSeries.map(withGrandTotal) : originalSeries,
                this.userOptions.eventSort
              );

              toggleButtonText(
                this.exportSVGElements,
                this.userOptions.eventSort.sortType,
                sortingTypes.NAME,
                sortingTypeBtnText.NAME,
                sortingTypeBtnText.SUM
              );

              this.redraw();
            },
          },
        }),
      },
      menuItemDefinitions: {
        grandTotal: {
          verticalAlign: 'bottom',
          align: 'left',
          text: toMenuItemTitle('grand total', true),
          onclick(e) {
            toggleMenuItemTitle(e, this, 'grandTotal', 'grand total');
            this.userOptions.grandTotal = !this.userOptions.grandTotal;
            showGrandTotal(this);
          },
        },
        averageLine: {
          verticalAlign: 'bottom',
          align: 'left',
          text: toMenuItemTitle('average', true),
          onclick(e) {
            toggleMenuItemTitle(e, this, 'averageLine', 'average');
            this.userOptions.averageLine = !this.userOptions.averageLine;
            this.redraw();
          },
        },
        medianLine: {
          verticalAlign: 'bottom',
          align: 'left',
          text: toMenuItemTitle('median', true),
          onclick(e) {
            toggleMenuItemTitle(e, this, 'medianLine', 'median');
            this.userOptions.medianLine = !this.userOptions.medianLine;
            this.redraw();
          },
        },
      },
    },
  };

  if (fromDashboard) {
    delete columnChartConfig.exporting.buttons.Fullscreen;
  }

  return columnChartConfig;
};
